<template>
  <Layout
    class="onboarding-book-selection"
    :title="page.title"
    :subtitle="subtitle"
    :scrollDefaultSlot="true"
  >
    <ul class="onboarding-book-selection__options">
      <li
        v-for="option in options"
        :key="option.id"
        class="onboarding-book-selection__option"
        :class="{
          'onboarding-book-selection__option--selected': isSelected(option.id),
        }"
        @click="toggleOption(option)"
      >
        <img
          :src="cacheBust(option.cover)"
          width="100"
          height="133"
          alt=""
          class="onboarding-book-selection__cover"
        />
        <div class="onboarding-book-selection__check">
          <span class="iconfont icheck-bold"></span>
        </div>
      </li>
    </ul>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { getQuestionnaireBooks, QuestionnaireView } from '@/models/onboarding'
import type {
  GenreSelectionChoice,
  BookSelectionPage,
  QuestionnaireBook,
} from '@/models/onboarding.types'
import { cacheBust } from '@/models/doc'

import Layout from '../Layout.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as BookSelectionPage
})

const selectedOptions = ref<QuestionnaireBook[]>([])

watch(selectedOptions, (newValue) => {
  // Sync the component state with the view,
  // but only send the properties we need.
  props.view.setCurrentChoice(
    newValue.map((option) => ({
      id: option.id,
      title: option.title,
      cover: option.cover,
    })),
  )

  // Only show the continue button once the user
  // has selected at least 3 options.
  if (newValue.length >= 3) {
    props.view.showContinueButton()
  } else {
    props.view.hideContinueButton()
  }
})

function toggleOption(option: QuestionnaireBook): void {
  const alreadySelected = selectedOptions.value.some(
    (oldValue) => oldValue.id === option.id,
  )

  const newValue = alreadySelected
    ? selectedOptions.value.filter((oldValue) => oldValue.id !== option.id)
    : [...selectedOptions.value, option]

  selectedOptions.value = newValue
}

function isSelected(optionID: string): boolean {
  return selectedOptions.value.some((option) => option.id === optionID)
}

const options = computed(() => {
  const selectedGenres =
    props.view.getUserChoice<GenreSelectionChoice>('genre-selection')

  return getQuestionnaireBooks(selectedGenres)
})

const subtitle = computed(() => {
  const booksSelectedNumber = selectedOptions.value.length

  if (booksSelectedNumber === 0) {
    return 'Select at least 3 books'
  }

  return `${booksSelectedNumber} books selected`
})

onMounted(() => {
  // The default value of `view.currentChoice` is `undefined`.
  // The value of the choices for this page is an array, so
  // we set the initial value of `view.currentChoice` to an
  // empty array so that we can spread the current value
  // without having to check if it's an array first.
  props.view.setCurrentChoice([])
})
</script>

<style lang="scss" scoped>
.onboarding-book-selection {
  overflow: hidden;

  &__options {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding: 0;
    @media (min-width: $w-tablet-b) {
      max-width: calc(var(--onboarding-inner-width) * 2);
      grid-template-columns: repeat(5, 1fr);
    }
  }

  &__option {
    position: relative;
    transition: all 0.1s;

    // Add hover effect only on desktop.
    @media (hover: hover) {
      &:hover {
        cursor: pointer;
        transform: scale(1.1);
        z-index: 1;
      }
    }

    > * {
      pointer-events: none;
    }
  }

  &__cover {
    width: 100%;
    filter: $shadow-f;
  }

  &__check {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    border: 3px solid $c-yellow;
    background: rgba($c-yellow, 0.2);
    opacity: 0;
    transition: all 0.1s;

    .iconfont {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 32px;
      height: 32px;
      border-radius: 32px;
      font-size: 20px;
      background: $c-yellow;
      border-color: $c-yellow;
      filter: $shadow-f;
    }
  }

  &__option--selected &__check {
    opacity: 1;
  }

  :deep(.onboarding-layout__default-slot) {
    // Make the default slot take the whole width of the screen,
    // so that it doesn't clip the book shadows.
    width: calc(100% + calc(var(--onboarding-padding) * 2));
    padding: var(--onboarding-padding);
    // Add some extra padding at the bottom to account for
    // the questionnaire button that is absolute positioned.
    padding-bottom: calc(var(--onboarding-padding) * 4);
  }

  :deep(~ .onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: var(--onboarding-padding);
    background: linear-gradient(
      0deg,
      rgba($c-bg, 0.8) 0%,
      rgba($c-bg, 0.8) 70%,
      rgba($c-bg, 0) 100%
    );
    z-index: 99;

    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      height: 100%;
      width: 100%;
      background: transparent;
      backdrop-filter: blur(3px);
      mask: linear-gradient(
        0deg,
        rgb(0, 0, 0) 0%,
        rgb(0, 0, 0) 77%,
        rgba(0, 0, 0, 0) 100%
      );
    }

    .onboarding-questionnaire__button {
      margin-top: 0;
    }
  }
}
</style>
